import React, { useState } from "react";
import CustomInput from "../CustomInput";
import { uploadBytes, getDownloadURL, ref as storageRef } from "firebase/storage";
import { storage } from "../../firebase/firebaseConfig";
import { authenticatedJson, ensureGuestSession } from "../../utils/guestSession";
import { functionsBaseUrl } from "../../config/runtime";

function QuickContactDialog({ shop, onClose }) {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        message: "",
        image: null,
    });
    const [imagePreview, setImagePreview] = useState(null);
    const [selectedTopics, setSelectedTopics] = useState([]);
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [isSent, setIsSent] = useState(false);
    const [isSending, setIsSending] = useState(false);


    const services = [
        ...(shop?.selectedSupplies || []),
        ...(shop?.customSupplies || []),
    ];
    // const topics = [
    //     "Paper & Cardstock",
    //     "Printer Toner & Ink",
    //     "Binding Supplies",
    //     "General enquiry",
    // ];


    const toggleTopic = (topic) => {
        setSelectedTopics((prev) => {
            if (prev.includes(topic)) {
                return prev.filter((t) => t !== topic); // remove
            } else {
                return [...prev, topic]; // add
            }
        });
    };

    const removeTopic = (topic) => {
        setSelectedTopics((prev) => prev.filter((t) => t !== topic));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];

        if (!file) return;

        setFormData((prev) => ({
            ...prev,
            image: file,
        }));

        setImagePreview(URL.createObjectURL(file));
    };

    const removeImage = () => {
        setFormData((prev) => ({
            ...prev,
            image: null,
        }));

        if (imagePreview) {
            URL.revokeObjectURL(imagePreview);
        }

        setImagePreview(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.name || !formData.email || !formData.message) return;

        try {
            setIsSending(true);
            let imageURL = null;
            const guest = await ensureGuestSession();

            // 1. Upload image to Firebase Storage (if exists)
            if (formData.image) {
                const imageRef = storageRef(storage, `enquiries/${guest.uid}/${shop?.uid || "unknown"}/${Date.now()}_${formData.image.name}`);

                await uploadBytes(imageRef, formData.image);

                imageURL = await getDownloadURL(imageRef);
            }

            await authenticatedJson(`${functionsBaseUrl}/submitShopEnquiry`, {
                shopUid: shop?.uid,
                formData,
                topics: selectedTopics,
                imageURL,
            });

        } catch (error) {
            console.error("Error sending enquiry:", error);
            alert("Failed to send message");
        } finally {
            setIsSending(false);
        }
    };

    const isValid =
        formData.name.trim() &&
        formData.email.trim() &&
        formData.message.trim();

    const handleDone = () => {
        setIsSent(false);
        onClose?.();

        setFormData({
            name: "",
            email: "",
            phone: "",
            message: "",
            image: null,
        });

        setSelectedTopics([]);
        setImagePreview(null);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-[16px]">
            {/* Overlay */}
            <div
                className="absolute inset-0 bg-black/40 backdrop-blur-[2px]"
                onClick={onClose}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-[16px] w-full max-w-[480px] max-h-[90vh] overflow-y-auto shadow-[0px_8px_40px_rgba(0,0,0,0.15)]">

                {/* Header */}
                <div className="sticky top-0 bg-white border-b border-[#e4e5e7] px-[24px] py-[16px] flex items-center justify-between rounded-t-[16px] z-10">
                    <div>
                        <h2 className="font-semibold text-[17px] text-[#212326]">
                            Contact {shop?.shopName || "Print Shop"}
                        </h2>
                        <p className="text-[13px] text-[#717182]">
                            {shop?.address ? shop.address.street || shop.address.city || shop.address.province || "No address provided" : "No address provided"}
                        </p>
                    </div>

                    <button
                        onClick={handleDone}
                        className="w-[32px] h-[32px] rounded-[10px] flex items-center justify-center hover:bg-[#f4f5f6]"
                    >
                        ✕
                    </button>
                </div>
                {isSent ? (
                    <div className="px-[24px] py-[48px] text-center">
                        <div className="w-[56px] h-[56px] rounded-full bg-[#ecfdf5] flex items-center justify-center mx-auto mb-[16px]">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                                viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                className="w-[28px] h-[28px] text-[#059669]">
                                <circle cx="12" cy="12" r="10"></circle>
                                <path d="m9 12 2 2 4-4"></path>
                            </svg>
                        </div>

                        <h3 className="font-semibold text-[18px] text-[#212326] mb-[6px]">
                            Message sent!
                        </h3>

                        <p className="font-medium text-[14px] text-[#717182] mb-[24px] max-w-[320px] mx-auto leading-[1.6]">
                            {shop?.shopName || "This shop"} will get back to you shortly via email or phone.
                        </p>

                        <button
                            onClick={handleDone}
                            className="px-[24px] py-[11px] rounded-[10px] bg-[#ff5323] text-white font-semibold text-[14px] hover:bg-[#e64a1f] transition-colors"
                        >
                            Done
                        </button>
                    </div>
                ) : (
                    <form
                        onSubmit={handleSubmit}
                        className="px-[24px] py-[20px] flex flex-col gap-[16px]"
                    >
                        <p className="font-medium text-[13px] text-[#717182] leading-[1.5]">
                            Have a question about printing, pricing, or turnaround times? Send a quick message and the store will get back to you.
                        </p>

                        {/* Dropdown */}
                        <div className="flex flex-col gap-[5px] relative">
                            <label className="font-semibold text-[13px] text-[#212326]">
                                What’s this about? <span className="text-[#9298a0]">(optional)</span>
                            </label>

                            <button
                                type="button"
                                onClick={() => setDropdownOpen(!dropdownOpen)}
                                className="w-full h-[44px] relative rounded-[10px] border border-[#e4e5e7] px-[14px] flex justify-between items-center text-[14px] text-[#212326] bg-white"
                            >
                                {selectedTopics.length > 0
                                    ? `${selectedTopics.length} topic${selectedTopics.length > 1 ? "s" : ""} selected`
                                    : "Select services or topics"}
                                <span className="text-[#9298a0]">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                                        className="lucide lucide-chevron-down absolute right-[12px] top-1/2 -translate-y-1/2 w-[16px] h-[16px] text-[#9298a0] transition-transform">
                                        <path d="m6 9 6 6 6-6"></path>
                                    </svg>
                                </span>
                            </button>

                            {dropdownOpen && (
                                <div className="absolute top-[70px] left-0 w-full bg-white border border-[#e4e5e7] rounded-[10px] shadow-md z-20 overflow-hidden">
                                    {services.map((t) => {
                                        const isSelected = selectedTopics.includes(t);

                                        return (
                                            <button
                                                key={t}
                                                type="button"
                                                onClick={() => toggleTopic(t)}
                                                className={`w-full px-[14px] py-[10px] text-left font-medium text-[13px] flex items-center justify-between transition-colors border-b border-[#f4f5f6] last:border-b-0
                            ${isSelected
                                                        ? "bg-[#fff5f2] text-[#ff5323]"
                                                        : "text-[#212326] hover:bg-[#f8f8fa]"
                                                    }`}
                                            >
                                                {t}

                                                {isSelected && (
                                                    <span className="w-[18px] h-[18px] rounded-[5px] bg-[#ff5323] flex items-center justify-center">
                                                        <svg
                                                            className="w-[10px] h-[10px] text-white"
                                                            viewBox="0 0 10 10"
                                                            fill="none"
                                                            stroke="currentColor"
                                                            strokeWidth="2"
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                        >
                                                            <path d="M1.5 5.5L4 8L8.5 2"></path>
                                                        </svg>
                                                    </span>
                                                )}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                            {selectedTopics.length > 0 && (
                                <div className="flex flex-wrap items-center gap-[6px] mt-[8px]">

                                    {selectedTopics.map((topic) => (
                                        <span
                                            key={topic}
                                            className="inline-flex items-center gap-[6px] bg-[#fff5f2] border border-[#ffe4db] text-[#ff5323] font-semibold text-[12px] px-[10px] py-[4px] rounded-[8px]"
                                        >
                                            {topic}

                                            <button
                                                type="button"
                                                onClick={() => removeTopic(topic)}
                                                className="hover:text-[#c73e15] transition-colors"
                                            >
                                                <svg
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    width="24"
                                                    height="24"
                                                    viewBox="0 0 24 24"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="2"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    className="w-[12px] h-[12px]"
                                                >
                                                    <path d="M18 6 6 18"></path>
                                                    <path d="m6 6 12 12"></path>
                                                </svg>
                                            </button>
                                        </span>
                                    ))}

                                </div>
                            )}
                        </div>

                        {/* Name */}
                        <CustomInput
                            label="Name"
                            required
                            placeHolder="Your full name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange} />

                        {/* Email */}
                        <CustomInput
                            label="Email"
                            required
                            placeHolder="you@email.com"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            type="email" />

                        {/* Phone */}
                        <CustomInput
                            label="Cell Number"
                            placeHolder="+27 82 123 4567"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange} />

                        {/* Message */}
                        <div className="flex flex-col gap-[5px]">
                            <label className="font-semibold text-[13px] text-[#212326]">
                                Your Question <span className="text-[#ff5323]">*</span>
                            </label>

                            <textarea
                                name="message"
                                placeHolder="e.g. How much would it cost to print 50 A3 colour posters?"
                                value={formData.message}
                                onChange={handleChange}
                                rows={4}
                                className="w-full rounded-[10px] border border-[#e4e5e7] px-[14px] py-[12px] font-medium text-[14px] text-[#212326] placeholder:text-[#9298a0] focus:outline-none focus:border-[#ff5323] transition-colors resize-none"
                            />
                        </div>

                        {/* Attachment (styled like original) */}
                        <div className="flex flex-col gap-[5px]">
                            <label className="font-semibold text-[13px] text-[#212326]">
                                Attach Image <span className="text-[#9298a0]">(optional - for quote assessment)</span>
                            </label>

                            {imagePreview ? (
                                <div className="relative rounded-[10px] border border-[#e4e5e7] overflow-hidden">
                                    <img
                                        src={imagePreview}
                                        alt="Attached"
                                        className="w-full h-[140px] object-cover"
                                    />

                                    <div className="absolute inset-0 bg-black/5"></div>

                                    <div className="absolute bottom-[8px] left-[8px] right-[8px] flex items-center justify-between">
                                        <span className="px-[10px] py-[4px] rounded-[6px] bg-white/90 text-[11px] font-medium text-[#212326] truncate max-w-[200px]">
                                            {formData.image?.name}
                                        </span>

                                        <button
                                            type="button"
                                            onClick={removeImage}
                                            className="w-[28px] h-[28px] rounded-[8px] bg-white/90 flex items-center justify-center hover:bg-white transition-colors"
                                        >
                                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                                                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                                                class="lucide lucide-trash2 lucide-trash-2 w-[14px] h-[14px] text-[#d32f2f]">
                                                <path d="M3 6h18"></path>
                                                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                                                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                                                <line x1="10" x2="10" y1="11" y2="17"></line>
                                                <line x1="14" x2="14" y1="11" y2="17"></line>
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                            ) : (
                                <label className="flex items-center justify-center gap-[8px] w-full h-[80px] border border-dashed border-[#d0d2d6] rounded-[10px] bg-[#fafafa] cursor-pointer hover:border-[#ff5323] hover:bg-[#fff8f6] transition-colors">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"
                                        fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                        className="w-[18px] h-[18px] text-[#9298a0]">
                                        <path d="M16 5h6"></path>
                                        <path d="M19 2v6"></path>
                                        <path d="M21 11.5V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7.5"></path>
                                        <path d="m21 15-3.086-3.086a2 2 0 0 0-2.828 0L6 21"></path>
                                        <circle cx="9" cy="9" r="2"></circle>
                                    </svg>

                                    <span className="font-medium text-[13px] text-[#717182]">
                                        Click to add a photo or screenshot
                                    </span>

                                    <input
                                        type="file"
                                        accept="image/*"
                                        className="hidden"
                                        onChange={handleFileChange}
                                    />
                                </label>
                            )}
                        </div>

                        {/* Submit */}
                        <button
                            type="submit"
                            disabled={!isValid || isSending}
                            className={`h-[46px] rounded-[10px] font-semibold flex items-center justify-center gap-[8px] transition-all
                        ${isValid
                                    ? "bg-[#ff5323] text-white hover:bg-[#e64a1f] shadow-[0px_2px_8px_rgba(255,83,35,0.3)]"
                                    : "bg-[#e4e5e7] text-[#9298a0] cursor-not-allowed"
                                }`}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                                class="lucide lucide-send w-[14px] h-[14px]"
                            >
                                <path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z"></path>
                                <path d="m21.854 2.147-10.94 10.939"></path>
                            </svg>
                            {isSending ? "Sending..." : "Send Message"}
                        </button>

                    </form>
                )}
            </div>
        </div>
    );
}

export default QuickContactDialog;
